import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, BellRing, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shown inside the booking section when no information day dates have
 * been confirmed yet. Lets visitors leave their details so the CLO can
 * notify them as soon as dates are announced. Posts to /api/feedback
 * with a tagged subject so it's easy to filter in admin.
 */
export default function RegisterInterest() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Read directly from DOM — defensive against browser autofill not firing onChange
    const formEl = e.currentTarget;
    const domName = (formEl.querySelector('[data-testid="ri-full-name"]')?.value || form.full_name).trim();
    const domEmail = (formEl.querySelector('[data-testid="ri-email"]')?.value || form.email).trim();
    const domPhone = (formEl.querySelector('[data-testid="ri-phone"]')?.value || form.phone).trim();
    const domNotes = (formEl.querySelector('[data-testid="ri-notes"]')?.value || form.notes).trim();

    if (domName.length < 2) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(domEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      const lines = [
        `Phone: ${domPhone || "—"}`,
        "",
        domNotes ||
          "Please notify me when information day dates are announced.",
      ];
      await axios.post(`${API}/feedback`, {
        full_name: domName,
        email: domEmail,
        subject: "Information day — register interest",
        message: lines.join("\n"),
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch as soon as dates are confirmed.");
      setForm({ full_name: "", email: "", phone: "", notes: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit — please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="register-interest">
      <div className="lg:col-span-5 bg-[#3A4B3A] text-white rounded-sm p-8 lg:p-10">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-white/10 border border-white/15 mb-5">
          <BellRing className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#CBA365] mb-3">
          Dates to be confirmed
        </p>
        <h3 className="font-serif-display text-2xl lg:text-3xl tracking-tight">
          Be the first to know when the information days are announced.
        </h3>
        <p className="mt-5 text-[14.5px] leading-relaxed text-white/80">
          We’re still finalising venue and dates. Leave your details and we’ll
          email you as soon as everything is confirmed, with the option to book
          a 30‑minute slot with the project team.
        </p>
        <ul className="mt-7 space-y-3 text-[13.5px] text-white/85">
          {[
            "Held locally at a community venue near Loughrea",
            "30‑minute one‑to‑one slots with the project team",
            "Drop‑in welcome too — no slot required",
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-2 inline-block h-1.5 w-1.5 rounded-sm bg-[#CBA365]" />
              {t}
            </li>
          ))}
        </ul>
      </div>

      {done ? (
        <div
          className="lg:col-span-7 bg-white border border-[#E0DCD3] rounded-sm p-8 lg:p-12 flex items-start gap-5"
          data-testid="register-interest-confirmation"
        >
          <CheckCircle2 className="h-7 w-7 text-[#3A4B3A] shrink-0 mt-1" />
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#CBA365]">
              You’re on the list
            </p>
            <h3 className="font-serif-display text-2xl tracking-tight mt-2">
              Thank you — we’ll be in touch soon.
            </h3>
            <p className="mt-3 text-[14.5px] text-[#2A2B2A]/85">
              As soon as the information day dates are confirmed, we’ll send you an
              email with venue details and a link to book your slot.
            </p>
            <button
              onClick={() => setDone(false)}
              data-testid="register-interest-add-another"
              className="mt-6 inline-flex items-center h-10 px-4 rounded-sm border border-[#4C4538] text-[#4C4538] text-sm font-medium hover:bg-[#4C4538] hover:text-white transition-colors"
            >
              Add another person
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-white border border-[#E0DCD3] rounded-sm p-7 lg:p-10"
          data-testid="register-interest-form"
        >
          <p className="overline mb-5">Your details</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Full name *">
              <input
                required
                minLength={2}
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="ri-full-name"
                className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
              />
            </Field>
            <Field label="Email *">
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="ri-email"
                className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="ri-phone"
                className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
              />
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Anything specific you’d like to discuss? (optional)">
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. screening near my property, traffic, hedgerow planting…"
                data-testid="ri-notes"
                className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30 resize-none"
              />
            </Field>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              data-testid="ri-submit"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-sm bg-[#3A4B3A] text-white text-sm font-medium hover:bg-[#2A3B2A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? "Submitting…" : "Notify me when dates are set"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium text-[#4C4538] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
